import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, LogOut, Send } from "lucide-react";
import { api } from "@/lib/api";
import type { BorradorPaso4Request, MiRegistroLegalResumen } from "../types";

interface SectorOption {
  id: string;
  codigo: string;
  nombre: string;
}

/**
 * Paso 4: resumen visual + sectores + consentimientos + envío.
 *
 * Cuando el cliente presiona "Enviar a revisión":
 *  1) PATCH /paso/4 con sectores y consentimientos.
 *  2) POST /enviar-revision. Si responde 422, mostramos los faltantes; si 204,
 *     navegamos al entry con el RL ya en EN_REVISION_QA.
 */
export function Paso4Revision({
  rl,
  onSalir,
  onAtras,
}: {
  rl: MiRegistroLegalResumen;
  onSalir: () => void;
  onAtras: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sectores, setSectores] = useState<string[]>(rl.sectoresAutorizados ?? []);
  const [consPriv, setConsPriv] = useState<boolean>(rl.consentimientoAvisoPrivacidad);
  const [tieneFil, setTieneFil] = useState<boolean>(rl.tieneFiliales);
  const [error, setError] = useState<string | null>(null);

  const qSectores = useQuery<SectorOption[]>({
    queryKey: ["sectores"],
    queryFn: async () => (await api.get<SectorOption[]>("/api/catalogos/sectores")).data,
    staleTime: 5 * 60_000,
  });

  const toggle = (id: string) =>
    setSectores((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // OBSERVADO viene del cliente subsanando: usa /reenviar-revision.
  // BORRADOR_INTERNO es la primera vez: usa /enviar-revision.
  const esReenvio = rl.estatus === "OBSERVADO";

  const enviar = useMutation({
    mutationFn: async () => {
      // 1) Guardar paso 4
      const body4: BorradorPaso4Request = {
        sectoresAutorizados: sectores,
        consentimientoAvisoPrivacidad: consPriv,
        tieneFiliales: tieneFil,
      };
      await api.patch(`/api/mi-registro-legal/borrador/${rl.id}/paso/4`, body4);
      // 2) Enviar (o reenviar) a revisión según el estatus actual
      const endpoint = esReenvio ? "reenviar-revision" : "enviar-revision";
      await api.post(`/api/mi-registro-legal/${rl.id}/${endpoint}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mi-registro-legal"] });
      navigate("/mi-registro-legal");
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    },
  });

  const guardarSinEnviar = useMutation({
    mutationFn: async () => {
      const body4: BorradorPaso4Request = {
        sectoresAutorizados: sectores,
        consentimientoAvisoPrivacidad: consPriv,
        tieneFiliales: tieneFil,
      };
      await api.patch(`/api/mi-registro-legal/borrador/${rl.id}/paso/4`, body4);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mi-registro-legal"] });
      onSalir();
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    },
  });

  return (
    <div className="space-y-4">
      <Card title="Resumen de tu información">
        <Dato label="Razón social" valor={rl.razonSocial} />
        <Dato label="Nombre comercial" valor={rl.nombreComercial ?? "—"} />
        <Dato label="RFC" valor={rl.rfc} mono />
        {rl.tipoPersona === "FISICA" && <Dato label="CURP" valor={rl.curp ?? "—"} mono />}
        <Dato label="Tipo de persona" valor={rl.tipoPersona === "MORAL" ? "Moral" : "Física"} />
        <Dato
          label="Roles"
          valor={[
            rl.puedeActuarComoTitular ? "Titular" : null,
            rl.puedeActuarComoTramitador ? "Tramitador" : null,
          ]
            .filter(Boolean)
            .join(" + ") || "—"}
        />
        <Dato label="Correo principal" valor={rl.correoPrincipal} />
        <Dato label="Teléfono" valor={rl.telefono} />
        <Dato label="Sitio web" valor={rl.paginaElectronica ?? "—"} />
        <Dato label="Giro" valor={rl.giroEmpresa ?? "—"} />
      </Card>

      {rl.domicilio && (
        <Card title="Domicilio fiscal">
          <Dato
            label="Dirección"
            valor={`${rl.domicilio.calle} ${rl.domicilio.numeroExterior}${
              rl.domicilio.numeroInterior ? " int. " + rl.domicilio.numeroInterior : ""
            }`}
            full
          />
          <Dato label="Colonia" valor={rl.domicilio.colonia} />
          <Dato label="Municipio" valor={rl.domicilio.municipio} />
          <Dato label="Entidad" valor={rl.domicilio.entidadFederativa} />
          <Dato label="CP" valor={rl.domicilio.codigoPostal} mono />
        </Card>
      )}

      <section className="rounded-lg border bg-surface shadow-card">
        <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          Sectores autorizados *
        </h2>
        <div className="p-4">
          <p className="mb-2 text-xs text-fg-subtle">
            Selecciona uno o más sectores en los que tu empresa busca certificarse.
          </p>
          <div className="flex flex-wrap gap-2">
            {(qSectores.data ?? []).map((s) => {
              const active = sectores.includes(s.id);
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => toggle(s.id)}
                  className={`rounded border px-3 py-1.5 text-sm ${
                    active
                      ? "border-primary bg-primary text-white"
                      : "border-edge-strong bg-surface text-fg-muted hover:bg-surface-2"
                  }`}
                >
                  {s.nombre}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="rounded-lg border bg-surface shadow-card">
        <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          Consentimientos
        </h2>
        <div className="space-y-3 p-4">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={consPriv}
              onChange={(e) => setConsPriv(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <strong>Acepto el aviso de privacidad *</strong>{" "}
              <span className="text-fg-muted">
                Doy mi consentimiento expreso para que NPMX trate mis datos personales conforme a su aviso de
                privacidad.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={tieneFil}
              onChange={(e) => setTieneFil(e.target.checked)}
              className="mt-0.5"
            />
            <span>Esta empresa tiene filiales o subsidiarias.</span>
          </label>
        </div>
      </section>

      <section className="rounded-lg border bg-estado-warn-bg p-4 text-sm text-estado-warn">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>Antes de enviar:</strong> verifica que toda tu información sea correcta. Una vez enviado,
            Calidad NPMX revisará tu solicitud y te responderá con observaciones, una aprobación (con folio) o
            un rechazo. Los documentos requeridos se solicitarán en una etapa posterior.
          </div>
        </div>
      </section>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border bg-estado-err-bg p-3 text-sm text-estado-err">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t bg-surface-2 px-4 py-3">
        <button
          type="button"
          onClick={() => guardarSinEnviar.mutate()}
          disabled={enviar.isPending || guardarSinEnviar.isPending}
          className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2 disabled:opacity-50"
        >
          <LogOut className="h-4 w-4" />
          Guardar y salir
        </button>
        <button
          type="button"
          onClick={onAtras}
          disabled={enviar.isPending || guardarSinEnviar.isPending}
          className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2 disabled:opacity-50"
        >
          ← Atrás
        </button>
        <button
          type="button"
          onClick={() => enviar.mutate()}
          disabled={enviar.isPending || guardarSinEnviar.isPending}
          className="inline-flex items-center gap-1 rounded bg-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
        >
          {enviar.isPending ? (
            <Send className="h-4 w-4 animate-pulse" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          {enviar.isPending ? "Enviando…" : esReenvio ? "Reenviar a revisión" : "Enviar a revisión"}
        </button>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border bg-surface shadow-card">
      <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">{title}</h2>
      <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

function Dato({ label, valor, mono, full }: { label: string; valor: string; mono?: boolean; full?: boolean }) {
  return (
    <div className={full ? "sm:col-span-2 lg:col-span-3" : ""}>
      <div className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className={`text-sm text-fg ${mono ? "font-mono" : ""}`}>{valor}</div>
    </div>
  );
}
